import "./index.css";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useTransitionStatus,
} from "@floating-ui/react";
import { clsx } from "clsx";
import { useCallback, useId, useMemo, useState } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { DialogContext } from "./context";
import type { IDialogContext, IDialogRootProps } from "./types";

// Matches --dialog-duration in index.css.
const TRANSITION_MS = 200;

// FloatingOverlay defaults to `overflow: auto` inline. A drawer sliding in from
// off-screen would overflow it and flash a scrollbar; `clip` also stops focus
// from scrolling it. The panel scrolls its own body, never the overlay.
const OVERLAY_STYLE = { overflow: "clip" } as const;

/**
 * Modal and Drawer core: open state, dismissal, focus, transition and the
 * title/description ids. Header, Body and Footer read it through context.
 */
export const DialogRoot = ({
  open,
  onOpenChange,
  closeOnOutsidePress = true,
  preventClose = false,
  ariaLabel,
  placement,
  className,
  ref,
  children,
}: IDialogRootProps) => {
  const titleId = useId();
  const descriptionId = useId();
  // Set by the Header, so the panel only points at ids that exist.
  const [hasTitle, setHasTitle] = useState(false);
  const [hasDescription, setHasDescription] = useState(false);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const { refs, context } = useFloating({ open, onOpenChange });

  const dismiss = useDismiss(context, {
    enabled: !preventClose,
    outsidePress: closeOnOutsidePress,
    outsidePressEvent: "mousedown",
  });
  const { getFloatingProps } = useInteractions([dismiss]);

  // Keeps the panel mounted until the exit transition ends.
  const { isMounted, status } = useTransitionStatus(context, {
    duration: reducedMotion ? 0 : TRANSITION_MS,
  });

  const panelRef = useMergeRefs([refs.setFloating, ref]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const value = useMemo<IDialogContext>(
    () => ({
      placement,
      preventClose,
      titleId,
      descriptionId,
      close,
      setHasTitle,
      setHasDescription,
    }),
    [placement, preventClose, titleId, descriptionId, close],
  );

  if (!isMounted) return null;

  return (
    <DialogContext value={value}>
      <FloatingPortal>
        <FloatingOverlay
          lockScroll
          style={OVERLAY_STYLE}
          className={clsx("nuco-dialog", `nuco-dialog--${placement}`)}
          data-status={status}
        >
          <FloatingFocusManager context={context} modal returnFocus>
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={hasTitle ? undefined : ariaLabel}
              aria-labelledby={hasTitle ? titleId : undefined}
              aria-describedby={hasDescription ? descriptionId : undefined}
              className={clsx("nuco-dialog__panel", className)}
              tabIndex={-1}
              {...getFloatingProps()}
            >
              {children}
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    </DialogContext>
  );
};
